import { logOut } from 'app/auth/AuthSlice';
import useApi, { ApiResponse } from 'common/hooks/useApi';
import useThunkDispatch from 'common/hooks/useThunkDispatch';
import useTypedSelector from 'common/hooks/useTypedSelector';
import { hasErrorCode } from 'common/services/api/hasErrorCode';
import { isActionCompleteFail } from 'common/services/api/isActionCompleteFail';
import { isActionCompleteSuccess } from 'common/services/api/isActionCompleteSuccess';
import absorbCancellation from 'common/services/cancel-promise/absorbCancellation';
import CancelError from 'common/services/cancel-promise/CancelError';
import AlertType from 'common/types/AlertType';
import { ActionCompleteResponse } from 'common/types/ApiResponse';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { Alerts } from './Alerts';
import DeactivatePresenter from './DeactivatePresenter';
import { DeactivateViews } from './DeactivateViews';

const initialState = {
  view: DeactivateViews.hidden,
  message: '',
  type: AlertType.info,
};

function DeactivateContainer() {
  const { push: pushHistory } = useHistory();
  const { profileName } = useTypedSelector((state) => {
    return {
      profileName: `${state.profile.data.firstName.trim()} ${state.profile.data.lastName.trim()}`,
    };
  });
  const [state, setState] = useState(initialState);
  const [
    { loading: isDeactivating },
    doDeactivate,
  ] = useApi<ActionCompleteResponse>({
    url: '/profile/deactivateAccount',
    method: 'PUT',
  });
  const [
    { loading: isDeactivatingAnyway },
    doDeactivateAnyway,
  ] = useApi<ActionCompleteResponse>({
    url: '/profile/deactivateAccWithApp?confirm=true',
    method: 'PUT',
  });

  const dispatch = useThunkDispatch();

  const handleSuccessOrContinue = async (
    res: ApiResponse<ActionCompleteResponse>
  ) => {
    if (isActionCompleteSuccess(res.data)) {
      dispatch(logOut());
      pushHistory('/', {
        message: Alerts.success,
        type: AlertType.info,
      });
      throw new CancelError();
    }
    return Promise.resolve(res);
  };

  const assertProfileName = async (name: string) => {
    if (!name || name.trim() !== profileName) {
      return Promise.reject(Alerts.confirmName);
    }
  };

  const handleFixableErrorsOrContinue = async (
    res: ApiResponse<ActionCompleteResponse>
  ) => {
    if (
      isActionCompleteFail(res.data) &&
      hasErrorCode(res.data, 'ACTIVE_APPLICATIONS_FOUND')
    ) {
      setState({
        view: DeactivateViews.confirmingWithApps,
        message: Alerts.confirmWithApps,
        type: AlertType.warning,
      });
      throw new CancelError();
    }
    return Promise.resolve(res);
  };

  const handleConfirmFail = (e: unknown) => {
    const msg = typeof e === 'string' ? e : Alerts.unhandled;
    setState((prev) => ({
      ...prev,
      message: msg,
      type: AlertType.danger,
    }));
  };

  const cancelAndShowError = (e: unknown) => {
    const msg = typeof e === 'string' ? e : Alerts.unhandled;
    setState({
      view: DeactivateViews.hidden,
      message: msg,
      type: AlertType.danger,
    });
  };

  const handleUnfixableApiErrors = async (
    res: ApiResponse<ActionCompleteResponse>
  ) => {
    if (
      isActionCompleteFail(res.data) &&
      hasErrorCode(res.data, 'ACTIVE_REGISTRATION_FOUND')
    ) {
      cancelAndShowError(Alerts.hasActiveRegs);
    } else {
      cancelAndShowError(Alerts.unhandled);
    }

    throw new CancelError();
  };

  const handleDeactivate = (name: string) =>
    assertProfileName(name)
      .then(() =>
        doDeactivate()
          .then(handleSuccessOrContinue)
          .then(handleFixableErrorsOrContinue)
          .then(handleUnfixableApiErrors)
          .catch(absorbCancellation)
      )
      .catch(handleConfirmFail);

  const handleDeactivateWithActiveApps = (name: string) =>
    assertProfileName(name)
      .then(() =>
        doDeactivateAnyway()
          .then(handleSuccessOrContinue)
          .then(handleUnfixableApiErrors)
          .catch(absorbCancellation)
      )
      .catch(handleConfirmFail);

  return (
    <DeactivatePresenter
      view={state.view}
      message={state.message}
      type={state.type}
      onStart={() =>
        setState({ ...initialState, view: DeactivateViews.confirming })
      }
      onCancel={() => setState(initialState)}
      onConfirm={handleDeactivate}
      onConfirmWithApps={handleDeactivateWithActiveApps}
      isDeactivating={isDeactivating || isDeactivatingAnyway}
      fullName={profileName}
    />
  );
}

export default DeactivateContainer;
